import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Box, Flex } from "@/components/layout/flexbox";
import { MonthlyEarnings } from "@/components/pages_public/sales-comp/utils/types";
import { MIDTEXT_COLOR } from "@/css/constants";

import { AcceleratorTooltip } from "./AcceleratorTooltip";
import { GuaranteeTooltip } from "./GuaranteeTooltip";
import {
  AcceleratorText,
  DarkerCell,
  DataCell,
  FooterAcceleratorCell,
  FooterCell,
  FooterGroupEndCell,
  FooterGuaranteeCell,
  FooterMonthCell,
  FooterSubText,
  FooterTotalCell,
  GroupEndCell,
  GuaranteeText,
  HeaderCell,
  MonthCell,
  PlaceholderDash,
  ProgressBar,
  ProgressBarContainer,
  QuotaSubText,
  StyledTable,
  SubText,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TierDot,
  TierIndicators,
  TotalPayCell,
} from "./TableStyles";

interface MonthlyEarningsTableProps {
  monthlyEarnings: MonthlyEarnings[];
  baseSalary: number;
  annualQuota: number;
  monthlyRampedQuotas: number[];
  commissionRate: number;
  accelerators: { threshold: number; multiplier: number }[];
}

function MonthlyEarningsTable({
  monthlyEarnings,
  baseSalary,
  annualQuota,
  monthlyRampedQuotas,
  commissionRate,
  accelerators,
}: MonthlyEarningsTableProps) {
  // Calculate adjusted commission rate
  const getAdjustedCommissionRate = (rampPercentage: number) => {
    return rampPercentage > 0
      ? (commissionRate * 100) / rampPercentage
      : commissionRate;
  };
  return (
    <Box mt={36}>
      <Box
        as="h2"
        mb={3}
        fontSize="18px"
        fontWeight={600}
        color={MIDTEXT_COLOR}
      >
        Monthly Earnings
      </Box>

      <TableContainer>
        <StyledTable>
          <TableHead>
            <tr>
              {/* Group 1: Basic Info */}
              <HeaderCell scope="col" isFirst>
                Month
              </HeaderCell>

              {/* Group 2: Sales Performance */}
              <HeaderCell scope="col">Quota (Ramped)</HeaderCell>
              <HeaderCell scope="col" isGroupEnd>
                Actual Revenue
              </HeaderCell>

              {/* Group 3: Compensation */}
              <HeaderCell scope="col">Base Salary</HeaderCell>
              <HeaderCell scope="col" darker>
                Base Commission
              </HeaderCell>
              <HeaderCell scope="col" guaranteeColor>
                Guarantee
              </HeaderCell>
              <HeaderCell scope="col" acceleratorColor>
                Accelerator
              </HeaderCell>
              <HeaderCell scope="col" isGroupEnd isTotal>
                Total Pay
              </HeaderCell>

              {/* Group 4: Quota Attainment */}
              <HeaderCell scope="col">% of Total Annual</HeaderCell>
              <HeaderCell scope="col">% of Ramped Annual</HeaderCell>
            </tr>
          </TableHead>
          <tbody>
            {monthlyEarnings.map((month) => {
              const isGuaranteeApplied = month.isProtectionApplied;
              const isAcceleratorApplied = month.isAccelerator;

              return (
                <TableRow
                  key={month.month}
                  isGuaranteeApplied={isGuaranteeApplied}
                  isAcceleratorApplied={isAcceleratorApplied}
                >
                  {/* Group 1: Basic Info */}
                  <MonthCell>{month.month}</MonthCell>

                  {/* Group 2: Sales Performance */}
                  <DataCell>
                    ${Math.round(month.quota).toLocaleString()}
                    <SubText>{month.rampPercentage}% of full</SubText>
                  </DataCell>
                  <GroupEndCell>
                    ${Math.round(month.commissionableRevenue).toLocaleString()}
                  </GroupEndCell>

                  {/* Group 3: Compensation */}
                  <DataCell>
                    ${Math.round(month.salary).toLocaleString()}
                  </DataCell>
                  <DarkerCell>
                    ${Math.round(month.baseCommission).toLocaleString()}
                  </DarkerCell>

                  <DataCell>
                    {month.isProtectionApplied ? (
                      <Tooltip
                        content={<GuaranteeTooltip month={month} />}
                        placement="top"
                        maxWidth="500px"
                      >
                        <GuaranteeText>
                          +$
                          {Math.round(
                            month.protectionAmount - month.earnedCommission,
                          ).toLocaleString()}
                        </GuaranteeText>
                      </Tooltip>
                    ) : (
                      <PlaceholderDash>—</PlaceholderDash>
                    )}
                  </DataCell>

                  <DataCell>
                    {month.isAccelerator ? (
                      <div>
                        <Tooltip
                          content={
                            <AcceleratorTooltip
                              month={month}
                              accelerators={accelerators}
                              getAdjustedCommissionRate={
                                getAdjustedCommissionRate
                              }
                            />
                          }
                          placement="top"
                          maxWidth="800px"
                        >
                          <AcceleratorText>
                            +$
                            {Math.round(
                              month.acceleratorAmount,
                            ).toLocaleString()}
                          </AcceleratorText>
                          {/* Small badge indicators */}
                          <TierIndicators>
                            {month.tierInfo.tierPortions.map((portion, idx) =>
                              portion > 0 ? (
                                <TierDot
                                  key={idx}
                                  color={
                                    idx === 0
                                      ? "#3b82f6"
                                      : idx === 1
                                        ? "#22c55e"
                                        : idx === 2
                                          ? "#9333ea"
                                          : idx === 3
                                            ? "#ef4444"
                                            : "#f97316"
                                  }
                                />
                              ) : null,
                            )}
                          </TierIndicators>
                        </Tooltip>
                      </div>
                    ) : (
                      <PlaceholderDash>—</PlaceholderDash>
                    )}
                  </DataCell>
                  <TotalPayCell>
                    ${Math.round(month.total).toLocaleString()}
                  </TotalPayCell>

                  {/* Group 4: Quota Attainment */}
                  <DataCell>
                    <Flex flexDirection="column">
                      <Flex alignItems="center">
                        <ProgressBarContainer>
                          <ProgressBar
                            width={Math.min(
                              100,
                              month.annualQuotaPercentage || 0,
                            )}
                            color={
                              (month.annualQuotaPercentage || 0) >= 100
                                ? "#9333ea"
                                : "#3b82f6"
                            }
                          />
                        </ProgressBarContainer>
                        <span>{month.annualQuotaPercentage || 0}%</span>
                      </Flex>
                      <QuotaSubText>
                        $
                        {month.totalRevenueSoFar.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}{" "}
                        / $
                        {annualQuota.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}
                      </QuotaSubText>
                    </Flex>
                  </DataCell>
                  <DataCell>
                    <Flex flexDirection="column">
                      <Flex alignItems="center">
                        <ProgressBarContainer>
                          <ProgressBar
                            width={Math.min(100, month.rampedQuotaPercentage)}
                            color={
                              month.rampedQuotaPercentage >= 100
                                ? "#22c55e"
                                : "#3b82f6"
                            }
                          />
                        </ProgressBarContainer>
                        <span>{month.rampedQuotaPercentage}%</span>
                      </Flex>
                      <QuotaSubText>
                        $
                        {month.totalRevenueSoFar.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}{" "}
                        / $
                        {monthlyRampedQuotas
                          .reduce((total, quota) => total + quota, 0)
                          .toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                      </QuotaSubText>
                    </Flex>
                  </DataCell>
                </TableRow>
              );
            })}
          </tbody>
          <TableFooter>
            <tr>
              {/* Group 1: Basic */}
              <FooterMonthCell>ANNUAL</FooterMonthCell>

              {/* Group 2: Sales Performance */}
              <FooterCell>
                ${Math.round(annualQuota).toLocaleString()}
                <FooterSubText>
                  Ramped: $
                  {Math.round(
                    monthlyEarnings.reduce(
                      (total, month) => total + month.quota,
                      0,
                    ),
                  ).toLocaleString()}
                </FooterSubText>
              </FooterCell>
              <FooterGroupEndCell>
                $
                {Math.round(
                  monthlyEarnings.reduce(
                    (total, month) => total + month.commissionableRevenue,
                    0,
                  ),
                ).toLocaleString()}
              </FooterGroupEndCell>

              {/* Group 3: Compensation */}
              <FooterCell>
                ${Math.round(baseSalary).toLocaleString()}
              </FooterCell>
              <FooterCell>
                $
                {Math.round(
                  monthlyEarnings.reduce(
                    (total, month) => total + month.baseCommission,
                    0,
                  ),
                ).toLocaleString()}
              </FooterCell>
              <FooterGuaranteeCell>
                +$
                {Math.round(
                  monthlyEarnings.reduce(
                    (total, month) =>
                      total +
                      (month.isProtectionApplied
                        ? month.protectionAmount - month.earnedCommission
                        : 0),
                    0,
                  ),
                ).toLocaleString()}
              </FooterGuaranteeCell>
              <FooterAcceleratorCell>
                +$
                {Math.round(
                  monthlyEarnings.reduce(
                    (total, month) => total + (month.acceleratorAmount || 0),
                    0,
                  ),
                ).toLocaleString()}
              </FooterAcceleratorCell>
              <FooterTotalCell>
                $
                {Math.round(
                  baseSalary +
                    monthlyEarnings.reduce(
                      (total, month) => total + month.commissionPaid,
                      0,
                    ),
                ).toLocaleString()}
              </FooterTotalCell>

              {/* Group 4: Quota Attainment */}
              <FooterCell>
                <Flex flexDirection="column">
                  <div>
                    {monthlyEarnings.length > 0
                      ? monthlyEarnings[monthlyEarnings.length - 1]
                          .annualQuotaPercentage || 0
                      : 0}
                    %
                  </div>
                </Flex>
              </FooterCell>
              <FooterCell>
                <Flex flexDirection="column">
                  <div>
                    {monthlyEarnings.length > 0
                      ? monthlyEarnings[monthlyEarnings.length - 1]
                          .rampedQuotaPercentage
                      : 0}
                    %
                  </div>
                </Flex>
              </FooterCell>
            </tr>
          </TableFooter>
        </StyledTable>
      </TableContainer>
    </Box>
  );
}

export default MonthlyEarningsTable;
